import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Flex,
  Heading,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import useThemeColors from "../../../../../hooks/useThemeColors";
import SaveButtons from "./SaveButtons";
import { textSecondary } from "../../../../../utils/colors";
import { updateTenantDentalChairsAction } from "../../../../../actions/tenantActions";
import { TENANT_UPDATE_DENTAL_CHAIRS_RESET } from "../../../../../constants/tenantConstants";

const MIN_CHAIRS = 1;
const MAX_CHAIRS = 10;

const ClinicDentalChairs = ({ slug, tenant }) => {
  const { backgroundLightMode } = useThemeColors();
  const toast = useToast();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const initialChairCount = Number.isFinite(tenant?.dentalChairs)
    ? tenant.dentalChairs
    : 3;

  const [chairCount, setChairCount] = useState(initialChairCount);
  const [showButtons, setShowButtons] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const chairCountValue = useMemo(() => initialChairCount, [initialChairCount]);

  const tenantUpdateDentalChairs = useSelector(
    (state) => state.tenantUpdateDentalChairs
  );
  const { success, error } = tenantUpdateDentalChairs;

  useEffect(() => {
    setChairCount(chairCountValue);
  }, [chairCountValue]);

  useEffect(() => {
    setShowButtons(chairCount !== chairCountValue);
  }, [chairCount, chairCountValue]);

  useEffect(() => {
    if (success && isSaving) {
      toast({
        title: t("clinicSettings.dentalChairsUpdate","Dental chairs updated"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setShowButtons(false);
    } else if (error && isSaving) {
      toast({
        title: t(
          "clinicSettings.dentalChairsUpdateError",
          "Unable to update dental chairs"
        ),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }

    if (success || error) {
      setIsSaving(false);
      dispatch({ type: TENANT_UPDATE_DENTAL_CHAIRS_RESET });
    }
  }, [success, error, dispatch, toast, t, isSaving]);

  const handleSave = () => {
    const parsed = Number(chairCount);
    if (!Number.isFinite(parsed) || parsed < MIN_CHAIRS || parsed > MAX_CHAIRS) {
      toast({
        title: t(
          "clinicSettings.dentalChairsInvalid",
          "Please enter a valid number of chairs"
        ),
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSaving(true);
    dispatch(
      updateTenantDentalChairsAction(slug, tenant.tenantID, {
        dentalChairs: parsed,
      })
    );
  };

  const handleCancel = () => {
    setChairCount(chairCountValue);
    setShowButtons(false);
  };

  return (
    <Box w="100%" p={["1.5rem 0", "3rem 0rem 3rem"]}>
      <Flex
        w="100%"
        direction="column"
        gap="1.25rem"
        bg={backgroundLightMode}
        borderRadius="1rem"
        p="1.25rem 1.5rem 1.25rem"
      >
        <Heading fontSize="1rem" fontWeight="600">
          {t("clinicSettings.dentalChairsHeading", "Dental chairs")}
        </Heading>
        <Text color={textSecondary} fontSize="sm">
          {t(
            "clinicSettings.dentalChairsDescription",
            "Set the number of operatory chairs available for scheduling (1-10)."
          )}
        </Text>

        <NumberInput
          min={MIN_CHAIRS}
          max={MAX_CHAIRS}
          value={chairCount}
          onChange={(valueAsString, valueAsNumber) => {
            if (Number.isFinite(valueAsNumber)) {
              setChairCount(valueAsNumber);
            } else {
              setChairCount(valueAsString);
            }
          }}
          clampValueOnBlur
          size="lg"
          maxW="7rem"
        >
          <NumberInputField />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
      </Flex>

      {showButtons && (
        <SaveButtons onClose={handleCancel} onClick={handleSave} />
      )}
    </Box>
  );
};

export default ClinicDentalChairs;
